<template>
  <div class="min-h-screen px-6 md:px-12 py-16 md:py-24">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <Motion
        as="div"
        class="text-center mb-16"
        :initial="{ opacity: 0, y: 30 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.8 }"
      >
        <h1 class="text-4xl md:text-6xl font-bold mb-6">
          Contact <span class="text-accent">Rob Rickard</span>
        </h1>
        <div class="max-w-3xl mx-auto">
          <p class="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Whether you're interested in partnerships, startup support, or
            innovation collaborations in Rwanda or beyond, feel free to get in
            touch.
          </p>
        </div>
        <div
          class="w-24 h-1 bg-gradient-to-r from-accent to-accent/30 mx-auto mt-8"
        ></div>
      </Motion>
      <!-- Office Locations -->
      <div class="space-y-12 mb-16">
        <Motion
          v-for="(location, index) in locations"
          :key="index"
          as="section"
          class="mb-12"
          :initial="{ opacity: 0, y: 30 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6, delay: index * 0.2 }"
        >
          <div class="flex items-center space-x-3 mb-8">
            <div
              class="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center"
            >
              <MapPin class="w-5 h-5 text-accent" />
            </div>
            <h2 class="text-2xl md:text-3xl font-bold">{{ location.title }}</h2>
          </div>
          <div class="grid gap-6">
            <Motion
              v-for="(addr, addrIndex) in location.addresses"
              :key="addrIndex"
              as="div"
              :initial="{ opacity: 0, x: -20 }"
              :animate="{ opacity: 1, x: 0 }"
              :transition="{ duration: 0.5, delay: addrIndex * 0.1 }"
              :whileHover="{ scale: 1.02, y: -2 }"
            >
              <div
                :whileHover="{ scale: 1.02, y: -2 }"
                class="flex items-start space-x-4 p-6 bg-gradient-to-r from-secondary/20 to-secondary/10 rounded-xl border border-secondary/30 hover:border-accent/30 transition-transform duration-300 hover:scale-105"
              >
                <div
                  class="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0 mt-1 transition-transform duration-300 hover:scale-105"
                >
                  <component :is="addr.icon" class="w-6 h-6 text-accent" />
                </div>
                <div class="flex-1">
                  <h3 class="font-bold text-lg mb-3">{{ addr.name }}</h3>
                  <div
                    class="text-muted-foreground whitespace-pre-line leading-relaxed"
                  >
                    {{ addr.address }}
                  </div>
                </div>
              </div>
            </Motion>
          </div>
        </Motion>
      </div>
      <!-- Email Section -->
      <Motion as="section" class="mb-16">
        <div class="flex items-center space-x-3 mb-8">
          <div
            class="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center"
          >
            <Mail class="w-5 h-5 text-accent" />
          </div>
          <h2 class="text-2xl md:text-3xl font-bold">Email</h2>
        </div>
        <Motion
          as="div"
          class="p-6 bg-gradient-to-r from-accent/10 to-accent/5 rounded-xl border border-green-500 border-opacity-20 hover:border-opacity-40 transition-transform duration-300 hover:scale-105"
          :whileHover="{ scale: 1.02, y: -2 }"
          :initial="{ opacity: 0, y: 30 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6 }"
        >
          <div class="flex items-center justify-between">
            <div>
              <h3 class="font-bold text-lg mb-2">
                General Inquiries & Direct Contact
              </h3>
              <a
                href="mailto:mailforRob@RobRickard.com"
                class="text-accent hover:text-accent/80 transition-colors text-lg font-medium"
              >
                📩 mailforRob@RobRickard.com
              </a>
            </div>
            <Mail className="w-8 h-8 text-accent/60" />
          </div>
        </Motion>
      </Motion>

      <Motion
        as="section"
        class="mb-16"
        :initial="{ opacity: 0, y: 30 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.6 }"
      >
        <div class="flex items-center space-x-3 mb-8">
          <div
            class="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center"
          >
            <Globe class="w-5 h-5 text-accent" />
          </div>
          <h2 class="text-2xl md:text-3xl font-bold">Related Platforms</h2>
        </div>

        <div class="grid gap-4">
          <Motion
            v-for="(platform, index) in platforms"
            :key="index"
            as="a"
            :href="platform.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center justify-between p-5 bg-gradient-to-r from-secondary/20 to-secondary/10 rounded-xl border border-secondary/30 hover:border-accent/30 transition-transform duration-300 hover:scale-105 group"
          >
            <div
              class="flex items-center space-x-4 hover:border-accent/30 transition-transform duration-300 hover:scale-105 group"
              :whileHover="{ scale: 1.02, y: -2 }"
              :initial="{ opacity: 0, x: -20 }"
              :animate="{ opacity: 1, x: 0 }"
              :transition="{ duration: 0.5, delay: index * 0.1 }"
            >
              <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              <span class="font-medium text-lg">{{ platform.name }}</span>
              <span class="text-muted-foreground">{{ platform.url }}</span>
            </div>
            <span>🔗</span>
          </Motion>
        </div>
      </Motion>

      <Motion
        as="div"
        class="text-center"
        :initial="{ opacity: 0, y: 30 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.6 }"
      >
        <div
          class="p-8 md:p-12 bg-gradient-to-br from-accent/10 to-accent/5 rounded-2xl border border-accent/20"
        >
          <h3 class="text-xl md:text-2xl font-bold mb-4">
            Let's Build Something Together
          </h3>
          <p class="text-muted-foreground mb-6 leading-relaxed">
            Ready to collaborate on innovative solutions, startup ventures, or
            technology initiatives? I'm always open to meaningful conversations
            about building the future of technology in Africa and beyond.
          </p>
          <a
            href="mailto:mailforRob@RobRickard.com"
            class="inline-flex items-center space-x-2 bg-accent text-background px-8 py-3 rounded-lg font-medium hover:bg-accent/90 transition-colors"
            :whileHover="{ scale: 1.05 }"
            :whileTap="{ scale: 0.95 }"
          >
            <Mail className="w-5 h-5" />
            <span>Send a Message</span>
          </a>
        </div>
        <div
          className="w-16 h-1 bg-gradient-to-r from-accent to-accent/30 mx-auto mt-12"
        ></div>
      </Motion>
    </div>
  </div>
</template>

<script setup>
import { useHead } from "@vueuse/head";
import { Motion } from "@motionone/vue";
import { MapPin, Mail, Globe, Building2, Coffee } from "lucide-vue-next";
import { ref } from "vue";

useHead({
  title: "Contact Rob Rickard",
  meta: [
    {
      name: "description",
      content:
        "Contact Rob Rickard for partnerships, startup support, or innovation collaborations in Rwanda and beyond.",
    },
    { property: "og:title", content: "Contact Rob Rickard" },
    {
      property: "og:description",
      content:
        "Get in touch with Rob Rickard, founder of RWBuild and Guhemba, for tech partnerships and innovation.",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://robrickard.com/contact" },
    {
      property: "og:image",
      content: "https://robrickard.com/img/robPortrait.65dd6588.png",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Rob Rickard",
        email: "mailforRob@RobRickard.com",
        worksFor: {
          "@type": "Organization",
          name: "RWBuild",
        },
        address: {
          "@type": "PostalAddress",
          addressLocality: "Kigali",
          addressCountry: "Rwanda",
        },
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: "mailforRob@RobRickard.com",
        },
      }),
    },
  ],
});

// Location data
const locations = ref([
  {
    title: "Rwanda Office",
    icon: Building2,
    addresses: [
      {
        name: "RWBuild Program – Tech Hub & Innovation Studio",
        address: "34 KN 41 Street, Kiyovu\nKigali, Rwanda",
        icon: Building2,
      },
      {
        name: "Rooftop Innovation Space – Ikawa Café",
        address: "Above RWBuild Hub\n34 KN 41 Street, Kiyovu\nKigali, Rwanda",
        icon: Coffee,
      },
    ],
  },
  {
    title: "United States Office",
    icon: Building2,
    addresses: [
      {
        name: "Common World Inc. (Parent Company of RWBuild)",
        address: "PO Box 365\nWauzeka, WI 53826\nUnited States",
        icon: Building2,
      },
    ],
  },
]);

// Platform links
const platforms = ref([
  {
    name: "Rwanda Build Program",
    url: "https://www.RwandaBuildProgram.rw",
  },
  {
    name: "Guhemba",
    url: "https://www.Guhemba.rw",
  },
  {
    name: "912Rwanda",
    url: "https://www.912Rwanda.com",
  },
]);
</script>
